// @flow
import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { makeVar } from '@apollo/client';
import {
  calcDurationToNow,
  TwoHrCallbackSubscription, useHandledSubscription, useInterval,
  useNewLeadsStatisticsSubscription,
  useTwoHrCallbackSubscription, useUrgentCallRequestsSubscription
} from '../../../shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRoute } from '../../router';
import moment from 'moment';
import _ from 'lodash';
import { NewLeadsStatisticsOutput, UrgentRequestCallOutput } from '@coverright/data-access/types/enrollment';
import { useCallAgendaMessage } from './hooks/use-call-agenda-message';
import { CallAgendaNotification } from './ui/call-agenda-notification';

export const hasUrgentToolbar = makeVar<boolean>(false);

export const UrgentNotifications = () => {
  const callAgendaMessage = useCallAgendaMessage();

  const {data: newLeadsData, } = useHandledSubscription(useNewLeadsStatisticsSubscription())
  const newLeads = newLeadsData?.newLeadsStatistics;

  const {data: urgentCallRequestsData} = useHandledSubscription(useUrgentCallRequestsSubscription());
  const urgentCallRequests = urgentCallRequestsData?.urgentCallRequests;

  const dateRange = useRef({
    from: moment.utc().startOf('month').toISOString(),
    to: moment.utc().add(1, 'week').toISOString()
  })
  const {data: twoHrData} = useHandledSubscription(useTwoHrCallbackSubscription({
    variables: {
      filter: {
        within2Hours: true,
        range: dateRange.current
      }
    }
  }));
  const twoHrCallback = twoHrData?.slas;

  const hasToolbar = useMemo(() => {
    return newLeads?.count ||
      twoHrCallback?.totalElements && twoHrCallback?.totalElements !== '0' ||
      urgentCallRequests?.length;
  }, [newLeads?.count, urgentCallRequests?.length, twoHrCallback?.totalElements])

  const minHeight = hasToolbar ? 30 : 0;

  const background = useMemo(() => {
    const colors: string[] = [];

    if (newLeads?.count) {
      colors.push('#03986B')
    }

    if (twoHrCallback?.totalElements && twoHrCallback?.totalElements !== '0') {
      colors.push('#F2BB2D')
    }

    if (urgentCallRequests?.length) {
      colors.push('#DE3124')
    }

    if (callAgendaMessage) {
      colors.push('#2466de')
    }

    setTimeout(() => hasUrgentToolbar(!!colors.length), 50);
    return generateDynamicGradientCSS(colors)
  }, [newLeads?.count, urgentCallRequests?.length, twoHrCallback?.totalElements, callAgendaMessage])

  return (
    <Box sx={{
      position: 'sticky',
      top: 0,
      zIndex: 2,
      minHeight,
      transition: 'all 0.3s ease-in-out',
      background,
      display: 'flex',
    }}>
      <NewLeadsStatistics data={newLeads} />
      <TwoHrCallback data={twoHrCallback} />
      <UrgentCallRequests data={urgentCallRequests} />
      <CallAgendaNotification data={callAgendaMessage} />
    </Box>
  );
};



const TwoHrCallback = ({data}: {data?: TwoHrCallbackSubscription['slas']}) => {
  const [time, setTime] = useState<string>();

  useInterval(() => {
    const oldestTime = _.min(data?.data?.map(s => s.createdAt))
    setTime(calcDurationToNow(oldestTime));
  }, 1000);

  if (!data || data?.totalElements === '0') {
    return null
  }

  return <Typography color={'warning.900'} variant={'body2'} flex={1} align={'center'} lineHeight={'30px'} noWrap>
    {data?.totalElements} Leads Due for 2hr Callback. Most Overdue: {time}
  </Typography>
}

const NewLeadsStatistics = ({data}: {data?: NewLeadsStatisticsOutput}) => {
  const [time, setTime] = useState<string>();

  useInterval(() => {
    setTime(calcDurationToNow(data?.oldestCreatedAt));
  }, 1000);

  if (!data?.count) {
    return null
  }

  let label = `${data.count} New lead${data.count > 1 ? 's' : ''}`;

  if (data?.nerdWalletCount) {
    if (data?.nerdWalletCount === data?.count) {
      label = `${data?.count} New NerdWallet lead${data.count > 1 ? 's' : ''}`
    } else {
      label += ` (${data.nerdWalletCount} NerdWallet lead${data.nerdWalletCount > 1 ? 's' : ''})`
    }
  }

  label += ` - Time to oldest: ${time}`

  return <Typography color={'white.main'} variant={'body2'} flex={1} align={'center'} lineHeight={'30px'} noWrap>
    {label}
  </Typography>
}

const UrgentCallRequests = ({data}: {data?: UrgentRequestCallOutput[]}) => {

  if (!data?.length) {
    return null
  }

  return (
    <Typography component={Link} color={'#FFF'} sx={{textDecoration: 'none'}} variant={'body2'}
                to={'/' + getRoute('urgentCalls')} flex={1} align={'center'} lineHeight={'30px'} noWrap>
      Urgent call requests: {data?.length}
    </Typography>
  )
}



function generateDynamicGradientCSS(colors: string[]): string {
  if (!colors || colors.length === 0) {
    return 'background: rgba(234, 234, 234, 1);';
  }

  const gradientStops: string[] = [];
  const fadeArea = 2;
  const mixArea = 10;
  const colorArea = (100 - (2 * fadeArea) - ((colors.length - 1) * mixArea)) / colors.length;

  // Start fade
  gradientStops.push(`rgba(234, 234, 234, 1) 0%`);
  gradientStops.push(`${colors[0]} ${fadeArea}%`);

  let currentPosition = fadeArea;

  for (let i = 0; i < colors.length; i++) {
    currentPosition += colorArea;
    gradientStops.push(`${colors[i]} ${currentPosition}%`);

    if (i < colors.length - 1) {
      currentPosition += mixArea;
      gradientStops.push(`${colors[i + 1]} ${currentPosition}%`);
    }
  }

  // End fade
  gradientStops.push(`${colors[colors.length - 1]} ${100 - fadeArea}%`);
  gradientStops.push(`rgba(234, 234, 234, 1) 100%`);

  return `linear-gradient(90deg, ${gradientStops.join(', ')})`;
}
